import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { CircleMarker, MapContainer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L, { type LatLngExpression, type PointTuple } from "leaflet";
import type { RouteShape, StopTime, VehicleLiveState, VehiclePosition } from "../api/types";
import { hasFreshFix, VEHICLE_LIVE_STATE_META } from "../api/types";
import { InvalidateOnResize, MapTileLayers, RouteNetworkOverlay, RouteStopsOverlay } from "./MapBaseLayers";
import { api } from "../api/client";
import { useReferenceData } from "../hooks/useReferenceData";
import { matchNearestSegment, describeNearestSegment, type StopCoord } from "../api/routeMatch";
import { formatAgo, initialMapBounds, rowLabel, stateExplanation, vehicleStatusLabel, type LiveVehicleRow } from "./liveFleet";
import { markerLookMeta, type MarkerLook } from "./markerLook";

// Identity lives with the row model now; re-exported for existing importers.
export { markerKey } from "./liveFleet";

const DEFAULT_CENTER: LatLngExpression = [33.6844, 73.0479]; // Rawalpindi/Islamabad
// Below this, plates along the corridor overlap into an unreadable band.
const VEHICLE_LABEL_ZOOM = 13;
const DEFAULT_ZOOM = 13;

interface FleetMapProps {
  /** Layout's joined rows keyed by marker key: state, plate, driver, trip and
   *  fix for every bus. One source, so a marker cannot disagree with its row
   *  in the list or with the counters. A row without a fix draws nothing. */
  rows: ReadonlyMap<string, LiveVehicleRow>;
  /** False until the first fleet snapshot (and the roster, device sessions and
   *  trips it is joined with) has arrived. Until then nothing is drawn: a
   *  state resolved from half the inputs — every bus "No GPS" for want of
   *  positions — is not a fact to put on a map. */
  liveKnown: boolean;
  /** Marker keys to draw — the route filter. Undefined draws every row. */
  visibleKeys?: ReadonlySet<string>;
  selectedVehicleId?: string;
  onSelectVehicle?: (id: string) => void;
  /** Bumped on every explicit selection (list click, search, Locate) so the
   *  map flies to the bus again even when it was already selected. */
  focusRequest?: number;
  /** Vehicle IDs with an active panic alarm. */
  panicVehicleIds?: ReadonlySet<string>;
  /** Counters and route filter, laid out beside the map's own controls. */
  hud?: ReactNode;
  /** Extra buttons for the top-right control cluster (e.g. full screen). */
  controls?: ReactNode;
  /** Whether the map may take its one-time opening view now: only while it
   *  is on screen in its final layout. Defaults to true. */
  fitOnLoad?: boolean;
  /** Active theme — switches the basemap tiles light/dark. */
  theme?: "dark" | "light";
}

/** How loudly the popup states each condition: idle buses are normal fleet
 *  time, not something to colour. */
const EXPLANATION_TONE: Record<VehicleLiveState, string | undefined> = {
  lost_contact: "crit-text",
  moving_no_trip: "warn-text",
  no_gps: "warn-text",
  offline: "warn-text",
  unclaimed: undefined,
  standing_by: undefined,
  on_trip: undefined,
};

/** Compact signed delay, e.g. "+3m" / "−45s". */
function fmtDelayShort(seconds?: number | null): string {
  if (seconds == null) return "—";
  const abs = Math.abs(seconds);
  const sign = seconds >= 0 ? "+" : "−";
  return abs < 60 ? `${sign}${abs}s` : `${sign}${Math.round(abs / 60)}m`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );
}

/**
 * Bus badge with an always-on identity chip underneath. Colour, glyph and
 * pulse come from markerLook — the same table the legend and the list read.
 */
function busIcon(
  look: MarkerLook,
  selected: boolean,
  plate: string,
  driver: string | undefined,
  trip: string | undefined,
  showChip: boolean,
): L.DivIcon {
  const meta = markerLookMeta(look);
  // Pulsing is kept for the emergencies (panic, a trip gone dark), so they are
  // also drawn larger and keep their label at every zoom.
  const urgent = !!meta.pulse;
  const size = selected ? 34 : urgent ? 30 : 26;
  const glyph = Math.round(size * 0.62);
  const pulse = urgent ? "veh-pulse" : "";
  const ring = selected ? "#ffffff" : "rgba(255,255,255,0.92)";

  // Only the selected bus carries driver and trip on its chip. Every marker
  // showing three lines turned the corridor into a wall of overlapping labels
  // wherever buses bunch — which is exactly where an operator most needs to
  // read them. The plate identifies the bus; the rest is one click away in the
  // popup, and appears inline the moment the marker is selected.
  // Zoomed out, the badges alone carry the fleet's shape and the plates just
  // collide into an unreadable band along the corridor. The selected bus and
  // anything urgent keep their label at every zoom, because those are the
  // two a dispatcher is actively tracking.
  const chip = !showChip && !selected && !urgent
    ? ""
    : `<span class="veh-plate">${escapeHtml(plate || "—")}</span>` +
    (selected && driver ? `<span class="veh-driver">${escapeHtml(driver)}</span>` : "") +
    (selected && trip ? `<span class="veh-trip">${escapeHtml(trip)}</span>` : "");

  return L.divIcon({
    html:
      `<div class="veh-marker">` +
        `<div class="veh-badge ${pulse}" style="width:${size}px;height:${size}px;background:${meta.color};border-color:${ring};">` +
          `<svg viewBox="0 0 24 24" width="${glyph}" height="${glyph}" fill="#fff"><path d="${meta.glyph}"/></svg>` +
        `</div>` +
        // No chip at all rather than an empty one: its border and padding
        // drew a stray white bar under every marker when zoomed out.
        (chip ? `<div class="veh-chip">${chip}</div>` : "") +
      `</div>`,
    className: "veh-bus-icon",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function hasCoords(p?: VehiclePosition): p is VehiclePosition {
  return !!p?.location && Number.isFinite(p.location.latitude) && Number.isFinite(p.location.longitude);
}

/**
 * Flies to the selected bus: when the selection changes, and again on each
 * explicit request (focusRequest) — pressing Enter on a search for the bus
 * already selected, after panning away, should still bring it back. Waits for
 * the bus's fix if the selection arrives first.
 */
function FlyToSelected({ rows, selectedVehicleId, focusRequest }: {
  rows: ReadonlyMap<string, LiveVehicleRow>;
  selectedVehicleId?: string;
  focusRequest?: number;
}) {
  const map = useMap();
  const flownRef = useRef<{ id?: string; request?: number }>({});
  useEffect(() => {
    if (!selectedVehicleId) { flownRef.current = {}; return; }
    const last = flownRef.current;
    if (last.id === selectedVehicleId && last.request === focusRequest) return;
    const p = rows.get(selectedVehicleId)?.position;
    if (!hasCoords(p)) return;
    flownRef.current = { id: selectedVehicleId, request: focusRequest };
    map.flyTo([p.location.latitude, p.location.longitude], Math.max(14, map.getZoom()), { duration: 0.6 });
  }, [selectedVehicleId, focusRequest, rows, map]);
  return null;
}

/**
 * Frames the route network, and any bus reporting live near it, once — when
 * the routes and the first fleet snapshot are both in. The map used to open
 * at a fixed centre and zoom over Islamabad, with the line running out of view
 * toward the airport: two of the three buses on the road that morning were
 * off-screen on load.
 *
 * Never again after that, and not at all once the operator has touched the
 * map or a bus is already selected: a view that re-frames itself under the
 * operator's hand, or every time data arrives, is worse than a fixed one.
 *
 * Only while `enabled`: the live map stays mounted behind every other page,
 * and a fit taken under the dashboard — wider, with no counters or vehicle
 * list beside it — left the corridor's ends under the counters and the legend
 * once the map itself was opened.
 */
function FitOnFirstLoad({ enabled, ready, shapes, rows, selected, padTopLeft, padBottomRight }: {
  enabled: boolean;
  ready: boolean;
  shapes: RouteShape[];
  rows: ReadonlyMap<string, LiveVehicleRow>;
  selected: boolean;
  padTopLeft: PointTuple;
  padBottomRight: PointTuple;
}) {
  const map = useMap();
  const settledRef = useRef(false);
  useEffect(() => {
    const el = map.getContainer();
    const settle = () => { settledRef.current = true; };
    // Any hand on the map — drag, wheel, pinch, a zoom button, the keyboard —
    // is the operator choosing their own view.
    el.addEventListener("pointerdown", settle);
    el.addEventListener("wheel", settle, { passive: true });
    el.addEventListener("keydown", settle);
    return () => {
      el.removeEventListener("pointerdown", settle);
      el.removeEventListener("wheel", settle);
      el.removeEventListener("keydown", settle);
    };
  }, [map]);
  useEffect(() => {
    if (!enabled || settledRef.current || !ready || shapes.length === 0) return;
    settledRef.current = true;
    if (selected) return;
    const fixes = [...rows.values()].map((r) => r.position).filter(hasCoords);
    const bounds = initialMapBounds(shapes, fixes, Date.now());
    if (!bounds) return;
    // The vehicle list may have appeared beside the map in this same render;
    // measure the container as it is now, not as Leaflet last saw it.
    map.invalidateSize({ pan: false });
    map.fitBounds(bounds, { paddingTopLeft: padTopLeft, paddingBottomRight: padBottomRight, maxZoom: 15, animate: false });
  }, [map, enabled, ready, shapes, rows, selected, padTopLeft, padBottomRight]);
  return null;
}

/**
 * The route network drawn beneath everything else.
 *
 * Deliberately quiet. This map exists to show where buses are; the lines are
 * context, so they sit in Leaflet's tilePane-adjacent overlay order below the
 * markers, carry a casing so they read on both light and dark basemaps, and
 * are drawn at reduced opacity. A route layer that competes with the vehicles
 * for attention has made the map worse, not better.
 *
 * The two directions of a route share a corridor almost exactly, so drawing
 * both would double the ink for no information. One line per route is drawn —
 * the lowest direction_id — and the geometry is the real road path from GTFS
 * shapes, not a straight hop between stops.
 */
/** Lifts the map's zoom to the parent so marker icons can respond to it. */
function ZoomReporter({ onZoom }: { onZoom: (z: number) => void }) {
  const map = useMap();
  useEffect(() => {
    const report = () => onZoom(map.getZoom());
    report();
    map.on("zoomend", report);
    return () => { map.off("zoomend", report); };
  }, [map, onZoom]);
  return null;
}

/**
 * The stops of the trip the selected bus is running, with their scheduled
 * times. The stop times come joined with each stop's name and position, so
 * nothing else has to load. The route itself is drawn by the highlighted route
 * line; a straight stop-to-stop line stands in only for a route with no shape.
 */
function SelectedTripStops({ stopTimes, drawLine }: { stopTimes?: StopTime[]; drawLine: boolean }) {
  const points = useMemo(() => {
    const out: { seq: number; lat: number; lon: number; name: string; code?: string; arrival: string }[] = [];
    for (const st of stopTimes ?? []) {
      if (st.stop_lat == null || st.stop_lon == null || !Number.isFinite(st.stop_lat) || !Number.isFinite(st.stop_lon)) continue;
      out.push({
        seq: st.stop_sequence,
        lat: st.stop_lat,
        lon: st.stop_lon,
        name: st.stop_name || "Stop",
        code: st.stop_code || undefined,
        arrival: st.arrival_time ?? "",
      });
    }
    return out.sort((a, b) => a.seq - b.seq);
  }, [stopTimes]);

  if (points.length === 0) return null;

  return (
    <>
      {drawLine ? (
        <Polyline
          positions={points.map((p) => [p.lat, p.lon] as LatLngExpression)}
          pathOptions={{ color: "#60a5fa", weight: 4, opacity: 0.7 }}
        />
      ) : null}
      {points.map((p) => (
        <CircleMarker
          key={p.seq}
          center={[p.lat, p.lon]}
          radius={4}
          pathOptions={{
            color: "white",
            weight: 1,
            fillColor: "#60a5fa",
            fillOpacity: 0.95,
          }}
        >
          <Popup>
            <div className="popup">
              <div><strong>{p.name}</strong></div>
              {p.code ? <div>Stop #{p.code}</div> : null}
              {p.arrival ? <div>Scheduled: {p.arrival.slice(0, 5)}</div> : null}
              <div>Seq: {p.seq}</div>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
}

export default function FleetMap({
  rows,
  liveKnown,
  visibleKeys,
  selectedVehicleId,
  onSelectVehicle,
  focusRequest,
  panicVehicleIds,
  hud,
  controls,
  fitOnLoad = true,
  theme = "dark",
}: FleetMapProps) {
  // Route lines. Fetched once — four short lines that change when the
  // timetable does, not per tick — and the on/off choice is remembered so an
  // operator who turned them off does not have to do it again every shift.
  const [mapZoom, setMapZoom] = useState<number>(DEFAULT_ZOOM);
  const [showRoutes, setShowRoutes] = useState<boolean>(() => {
    try { return localStorage.getItem("occ.map.routes") !== "off"; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("occ.map.routes", showRoutes ? "on" : "off"); } catch { /* private mode */ }
  }, [showRoutes]);
  // Route lines and stations: static reference data, fetched once per session
  // but retried rather than abandoned after a single failure. See the hook —
  // this is the pattern that silently lost the route network on every load.
  const routeShapesRef = useReferenceData(() => api.listRouteShapes(), { label: "route shapes" });
  const routeStopsRef = useReferenceData(() => api.listRouteStops(), { label: "route stops" });
  // Ordered per-direction stop sequences, for matching an untracked bus's raw
  // GPS fix onto the nearest route segment — listRouteStops() alone can't do
  // this, its sequence number is a DISTINCT-ON artifact, arbitrary across the
  // routes a stop appears on.
  const stopTemplatesRef = useReferenceData(() => api.listStopTemplates(), { label: "stop templates" });
  const routeShapes = useMemo(
    () => (Array.isArray(routeShapesRef.data) ? routeShapesRef.data : []),
    [routeShapesRef.data]
  );
  const routeStops = useMemo(
    () => (Array.isArray(routeStopsRef.data) ? routeStopsRef.data : []),
    [routeStopsRef.data]
  );
  const stopTemplates = useMemo(
    () => (Array.isArray(stopTemplatesRef.data) ? stopTemplatesRef.data : []),
    [stopTemplatesRef.data]
  );
  const stopCoordsById = useMemo(() => {
    const m = new Map<string, StopCoord>();
    for (const s of routeStops) {
      m.set(s.stop_id, { name: s.name, latitude: s.latitude, longitude: s.longitude });
    }
    return m;
  }, [routeStops]);

  const markers = useMemo(() => {
    if (!liveKnown) return [];
    const out: { row: LiveVehicleRow; p: VehiclePosition; look: MarkerLook }[] = [];
    for (const row of rows.values()) {
      const p = row.position;
      if (!hasCoords(p)) continue;
      if (visibleKeys && !visibleKeys.has(row.key)) continue;
      const isPanic = !!row.vehicleId && !!panicVehicleIds?.has(row.vehicleId);
      out.push({ row, p, look: isPanic ? "panic" : row.state });
    }
    return out;
  }, [rows, liveKnown, visibleKeys, panicVehicleIds]);

  // The selected bus's route. Tablet fixes carry no trip_id or route_id, so
  // keying this off the fix alone meant it never drew for a real bus: the trip
  // instance it is running names the timetable trip, and that trip names its
  // route and stops. The fix's own ids remain the fallback.
  const selectedRow = selectedVehicleId ? rows.get(selectedVehicleId) : undefined;
  const templateTripId = selectedRow?.trip?.trip_id || selectedRow?.position?.trip_id || undefined;
  const templateTripRef = useReferenceData(() => api.getTrip(templateTripId!), {
    label: "selected bus's trip",
    deps: [templateTripId],
    enabled: !!templateTripId,
  });
  // The hook keeps the previous trip while the next loads; never show one
  // bus's route under another.
  const templateTrip = templateTripRef.data?.id === templateTripId ? templateTripRef.data : undefined;
  const highlightRouteId = templateTrip?.route_id || selectedRow?.position?.route_id || undefined;
  const highlightShapes = useMemo(
    () => (highlightRouteId ? routeShapes.filter((s) => s.route_id === highlightRouteId) : []),
    [routeShapes, highlightRouteId]
  );

  const now = Date.now();
  // Clear of what floats over the live map: the counters and route pills
  // along the top, and the legend in the bottom-left corner — the corridor
  // runs out to the airport exactly there, and the first fit put that end of
  // the line under the legend.
  const padTopLeft = useMemo<PointTuple>(() => (hud ? [44, 116] : [24, 24]), [hud]);
  const padBottomRight = useMemo<PointTuple>(() => (hud ? [40, 170] : [24, 24]), [hud]);

  return (
    <div className="occ-map">
      {/* The counters and route filter (hud) and the map's own buttons share
          one row, so neither can be laid over the other: the full-screen
          button used to sit on top of the routes toggle, leaving "Routes"
          readable and the "on" under it. */}
      <div className="occ-map-hud">
        <div className="occ-map-hud-main">{hud}</div>
        <div className="occ-map-controls">
          {/* Routes on/off. Over the map rather than in a Leaflet
              LayersControl: one toggle does not justify an accordion, and an
              always-visible control is one click instead of two. */}
          <button
            type="button"
            className={"occ-map-toggle" + (showRoutes ? " on" : "")}
            onClick={() => setShowRoutes((v) => !v)}
            title={showRoutes ? "Hide route lines" : "Show route lines"}
            aria-pressed={showRoutes}
          >
            {showRoutes ? "Routes on" : "Routes off"}
          </button>
          {controls}
        </div>
      </div>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
        preferCanvas={false}
      >
        <MapTileLayers theme={theme} />
        <InvalidateOnResize />
        <ZoomReporter onZoom={setMapZoom} />
        <FitOnFirstLoad
          enabled={fitOnLoad}
          ready={liveKnown}
          shapes={routeShapes}
          rows={rows}
          selected={!!selectedVehicleId}
          padTopLeft={padTopLeft}
          padBottomRight={padBottomRight}
        />
        <FlyToSelected rows={rows} selectedVehicleId={selectedVehicleId} focusRequest={focusRequest} />

        {/* With the network hidden, the selected bus's route still shows. */}
        {showRoutes || highlightShapes.length > 0 ? (
          <RouteNetworkOverlay
            shapes={showRoutes ? routeShapes : highlightShapes}
            highlightRouteId={highlightRouteId}
            theme={theme}
          />
        ) : null}

        {showRoutes ? <RouteStopsOverlay stops={routeStops} theme={theme} /> : null}

        <SelectedTripStops stopTimes={templateTrip?.stop_times} drawLine={highlightShapes.length === 0} />

        {markers.map(({ row, p, look }) => {
          const key = row.key;
          const isSelected = key === selectedVehicleId;
          const trip = row.trip;
          const fresh = hasFreshFix(p, now);
          const tripChip = trip
            ? (trip.route_short_name || trip.headsign || trip.route_long_name || "On trip")
            : undefined;
          const fixAt = new Date(p.time).getTime();
          // The same sentence the detail panel uses; on a trip the trip lines
          // below say it better.
          const explanation = row.state === "on_trip" ? null : stateExplanation(row, now);
          // Leaflet stacks markers by latitude alone. Two buses parked at the
          // N-5 terminal drew the southern one on top even when the operator
          // had just selected the other, whose plate then sat hidden under the
          // wrong bus's. The selected bus goes on top, then the emergencies.
          const stack = isSelected ? 2000 : markerLookMeta(look).pulse ? 1000 : 0;
          return (
            <Marker
              key={key}
              position={[p.location.latitude, p.location.longitude]}
              icon={busIcon(look, isSelected, rowLabel(row), row.driver || undefined, tripChip, mapZoom >= VEHICLE_LABEL_ZOOM)}
              zIndexOffset={stack}
              eventHandlers={{ click: () => onSelectVehicle?.(key) }}
            >
              <Popup>
                <div className="popup">
                  <div>
                    <strong>Plate: {row.plate || "—"}</strong>
                    {row.inactiveStatus ? (
                      // On the map only because it is on the road.
                      <span className="popup-chip">{vehicleStatusLabel(row.inactiveStatus)}</span>
                    ) : null}
                  </div>
                  {row.kind === "tablet" ? <div>Tablet {row.deviceId} · no bus assigned</div> : null}
                  {row.kind === "unlisted" ? <div className="warn-text">Not on the fleet roster</div> : null}
                  <div>Driver: {row.driver || "—"}</div>
                  <div className={look === "panic" ? "crit-text" : undefined}>
                    Status: {look === "panic" ? "Panic alarm active" : VEHICLE_LIVE_STATE_META[row.state].label}
                  </div>
                  {explanation ? (
                    <div className={EXPLANATION_TONE[row.state]}>{explanation}</div>
                  ) : null}
                  {trip ? (
                    <>
                      <div>
                        {trip.route_short_name ? (
                          <span
                            className="route-badge"
                            style={trip.route_color ? { background: `#${trip.route_color}`, color: `#${trip.route_text_color || "FFFFFF"}` } : undefined}
                          >
                            {trip.route_short_name}
                          </span>
                        ) : null}
                        <span style={{ marginLeft: trip.route_short_name ? 6 : 0 }}>
                          {trip.headsign || trip.route_long_name || ""}
                        </span>
                      </div>
                      <div>
                        Trip: {trip.status.replace("_", " ")}
                        {trip.current_stop_sequence != null ? ` · stop ${trip.current_stop_sequence}` : ""}
                      </div>
                      <div>Delay: {fmtDelayShort(trip.delay_seconds)}</div>
                    </>
                  ) : null}
                  {row.signedOut && (row.state === "on_trip" || row.state === "lost_contact") ? (
                    // The tablet running this trip has had no working session
                    // for hours: the trip is open, but nothing is recording it.
                    <div className="warn-text">⚠ Tablet signed out for hours — this trip is not being recorded</div>
                  ) : null}
                  {row.state === "moving_no_trip" ? (() => {
                    // No trip means no real "next stop" — infer position from
                    // the fix alone, matched against every known route's stop
                    // sequence. Dispatchers asked for this specifically for
                    // buses in this state.
                    const match = matchNearestSegment(
                      p.location.latitude, p.location.longitude, stopTemplates, stopCoordsById
                    );
                    const desc = describeNearestSegment(match);
                    return desc ? <div>{desc}</div> : null;
                  })() : null}
                  {/* A stale fix's speed is history, not a reading. */}
                  <div>Speed: {fresh && p.speed != null ? p.speed.toFixed(1) + " km/h" : "—"}</div>
                  <div>Heading: {fresh && p.bearing != null ? `${p.bearing}°` : "—"}</div>
                  {p.occupancy_percentage != null ? (
                    <div>Occupancy: {p.occupancy_percentage}%</div>
                  ) : null}
                  <div>
                    {fresh ? "Updated" : "Last known position"}: {new Date(p.time).toLocaleTimeString()}
                    {Number.isFinite(fixAt) && !fresh ? ` (${formatAgo(now - fixAt)})` : ""}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
